import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, SearchX } from 'lucide-react';
import { api, type TicketState } from '../lib/api.js';
import { ago } from '../lib/format.js';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHeader,
  Panel,
  Skeleton,
  StateBadge,
} from '../ui/index.js';

interface Result {
  id: string;
  tenant_id: string;
  number: number;
  subject: string;
  state: TicketState;
  request_type: string | null;
  updated_at: string;
  requester_name: string | null;
}

/** Full-text search, scoped to the tenants the caller can see (FR-AG-07). */
export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';

  const res = useQuery({
    queryKey: ['search', q],
    queryFn: () =>
      api.get<{ results: Result[]; query: string }>(`/api/v1/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Search"
        description={
          q ? (
            <>
              Results for <strong className="text-ink">“{q}”</strong> — subjects, messages, people
              and form fields
            </>
          ) : (
            'Press ⌘K anywhere to search'
          )
        }
      />

      {res.isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-line bg-surface p-3">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="mt-2.5 h-3.5 w-3/4" />
            </div>
          ))}
        </div>
      )}

      {res.error && <ErrorState error={res.error} />}

      {res.data?.results.length === 0 && (
        <Panel flush>
          <EmptyState
            icon={<SearchX />}
            title="No matches"
            hint="Search covers subjects, every message and note, the requester and who the ticket is about, and form fields. Part of a word works too."
          />
        </Panel>
      )}

      <ul className="space-y-2">
        {res.data?.results.map((r) => (
          <li key={r.id}>
            <Link
              to={`/t/${r.tenant_id}/${r.number}`}
              className="group flex items-center gap-3 rounded-lg border border-line bg-surface p-3 shadow-xs transition-[background-color,box-shadow] hover:bg-surface-hover hover:shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-1.5">
                  <span className="nums text-2xs text-ink-tertiary">#{r.number}</span>
                  <StateBadge state={r.state} />
                  <Badge tone="neutral">{r.tenant_id}</Badge>
                </div>
                <p className="truncate text-sm font-medium text-ink">{r.subject}</p>
                {(r.requester_name || r.request_type) && (
                  <p className="mt-0.5 truncate text-2xs text-ink-tertiary">
                    {[r.requester_name, r.request_type].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-2xs text-ink-tertiary">{ago(r.updated_at)}</span>
              <ChevronRight
                aria-hidden
                className="h-4 w-4 shrink-0 text-ink-tertiary transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
